<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCountries, type Country } from "@/composables/useCountries";

const props = defineProps<{ modelValue: string }>();
const emit = defineEmits<{ "update:modelValue": [iso2: string] }>();

const { t } = useI18n();
const { countries } = useCountries();

const open = ref(false);
const search = ref("");
const focusedIndex = ref(0);

const containerRef = ref<HTMLDivElement | null>(null);
const searchRef = ref<HTMLInputElement | null>(null);
const listRef = ref<HTMLUListElement | null>(null);

const selected = computed<Country | null>(
  () => countries.value.find((c) => c.iso2 === props.modelValue) ?? null,
);

const filtered = computed<Country[]>(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return countries.value;
  return countries.value.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q.replace(/^\+/, "")) ||
      c.iso2.toLowerCase().includes(q),
  );
});

function toggle(): void {
  open.value = !open.value;
}

function close(): void {
  open.value = false;
  search.value = "";
}

function select(country: Country): void {
  emit("update:modelValue", country.iso2);
  close();
}

function onKeydown(event: KeyboardEvent): void {
  if (!open.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    close();
    return;
  }
  if (event.key === "ArrowDown") {
    event.preventDefault();
    focusedIndex.value = Math.min(focusedIndex.value + 1, filtered.value.length - 1);
    scrollToFocused();
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    focusedIndex.value = Math.max(focusedIndex.value - 1, 0);
    scrollToFocused();
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const country = filtered.value[focusedIndex.value];
    if (country) select(country);
  }
}

function scrollToFocused(): void {
  void nextTick(() => {
    const el = listRef.value?.querySelector<HTMLLIElement>(".country-picker__item--focused");
    el?.scrollIntoView({ block: "nearest" });
  });
}

function onDocumentClick(event: MouseEvent): void {
  if (!open.value) return;
  if (!containerRef.value?.contains(event.target as Node)) {
    close();
  }
}

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

watch(open, (isOpen) => {
  if (!isOpen) return;
  const initialIndex = filtered.value.findIndex((c) => c.iso2 === props.modelValue);
  focusedIndex.value = initialIndex >= 0 ? initialIndex : 0;
  void nextTick(() => {
    searchRef.value?.focus();
    scrollToFocused();
  });
});

watch(search, () => {
  focusedIndex.value = 0;
});
</script>

<template>
  <div ref="containerRef" class="country-picker" @keydown="onKeydown">
    <button
      type="button"
      class="country-picker__trigger"
      :aria-haspopup="'listbox'"
      :aria-expanded="open"
      :aria-label="t('whatsapp.countryPick')"
      @click="toggle"
    >
      <span
        v-if="selected"
        :class="['fi', `fi-${selected.iso2.toLowerCase()}`]"
        class="country-picker__flag"
        aria-hidden="true"
      />
      <span class="country-picker__dial">+{{ selected?.dialCode ?? "" }}</span>
      <svg
        class="country-picker__chevron"
        :class="{ 'country-picker__chevron--open': open }"
        width="10"
        height="6"
        viewBox="0 0 10 6"
        aria-hidden="true"
      >
        <path
          d="M1 1l4 4 4-4"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div v-if="open" class="country-picker__popover" role="dialog">
      <input
        ref="searchRef"
        v-model="search"
        class="country-picker__search gate-input"
        type="text"
        :placeholder="t('whatsapp.countrySearch')"
        autocomplete="off"
        spellcheck="false"
      />
      <ul
        ref="listRef"
        class="country-picker__list"
        :aria-label="t('whatsapp.countryPick')"
        role="listbox"
      >
        <li
          v-for="(country, index) in filtered"
          :key="country.iso2"
          class="country-picker__item"
          :class="{
            'country-picker__item--focused': index === focusedIndex,
            'country-picker__item--selected': country.iso2 === modelValue,
          }"
          role="option"
          :aria-selected="country.iso2 === modelValue"
          @click="select(country)"
          @mouseenter="focusedIndex = index"
        >
          <span
            :class="['fi', `fi-${country.iso2.toLowerCase()}`]"
            class="country-picker__flag"
            aria-hidden="true"
          />
          <span class="country-picker__name">{{ country.name }}</span>
          <span class="country-picker__dial-muted">+{{ country.dialCode }}</span>
        </li>
        <li v-if="filtered.length === 0" class="country-picker__empty">
          {{ t("whatsapp.countryEmpty") }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.country-picker {
  position: relative;
  display: inline-flex;
}

.country-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-3);
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: var(--font-size-md);
  color: var(--color-text);
  min-height: var(--tap-target);
  cursor: pointer;
  white-space: nowrap;
  transition: border-color var(--transition-fast);
}

.country-picker__trigger:hover {
  border-color: var(--color-primary);
}

.country-picker__trigger:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.country-picker__flag {
  width: 20px;
  height: 15px;
  border-radius: 2px;
  flex-shrink: 0;
}

.country-picker__dial {
  font-variant-numeric: tabular-nums;
}

.country-picker__chevron {
  transition: transform var(--transition-fast);
  color: var(--color-text-secondary);
}

.country-picker__chevron--open {
  transform: rotate(180deg);
}

.country-picker__popover {
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 0;
  right: 0;
  min-width: min(320px, calc(100vw - var(--space-8)));
  max-width: calc(100vw - var(--space-4));
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  z-index: 20;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.country-picker__search {
  margin: var(--space-3);
  min-height: 40px;
}

.country-picker__list {
  max-height: 280px;
  overflow-y: auto;
  padding-bottom: var(--space-2);
}

.country-picker__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  font-size: var(--font-size-md);
  color: var(--color-text);
}

.country-picker__item--focused {
  background-color: var(--color-primary-muted);
}

.country-picker__item--selected .country-picker__name {
  font-weight: 600;
  color: var(--color-primary);
}

.country-picker__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.country-picker__dial-muted {
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.country-picker__empty {
  padding: var(--space-4);
  text-align: center;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}
</style>
